import classNames from 'classnames';
import _ from 'lodash';
import React from 'react';
import { Position } from '../../../index';
import {
  ASYNC_UPLOAD_DISPATCHER_NAME,
  UPLOAD_REQUEST_CANCELED,
  UPLOAD_REQUEST_CANCEL_SCHEDULED
} from '../../config/Constants';
import Dispatcher from '../../Dispatcher';
import Component from '../component/Component';
import { 
  PROGRESS_BAR_PROPS, 
  PROGRESS_BAR_STATE, 
  ProgressType, 
  UPLOAD_FILE_DATA
} from './Types';

class ProgressBar extends Component<PROGRESS_BAR_PROPS, PROGRESS_BAR_STATE> {
  private lastProgress: number;
  static defaultProps = {
    keepPercentagesWhenUploaded: false,
    progressLabelPosition: 'right',
    cancelable: false,
    showFileName: false,
    infiniteAnimate: false,
    hidePercentage: false
  };

  cancelRequest = () => {
    const { fileName, uploadGroup } = this.getUploadGroupAndFileName();
    this.dispatch(
      'DispatcherUploadProgressCancel',
      {
        pos: Position.start,
        dispatcherId: ASYNC_UPLOAD_DISPATCHER_NAME
      },
      {
        cancelRequestForFile: fileName,
        cancelRequestForUploadGroup: uploadGroup
      }
    );
    if (_.isFunction(this.props.onCancel)) {
      this.props.onCancel();
    }
  };

  isCanceled = (progress: number): boolean => progress === UPLOAD_REQUEST_CANCELED || progress === UPLOAD_REQUEST_CANCEL_SCHEDULED;

  getProgressContent = (progress: number): any => {
    const content = this.isCanceled(progress) ? this.translate('canceled') : progress + '%';
    let attrs: any = {
      // <debug>
      'data-cy': 'ProgressBar-content'
      // </debug>
    };
    return (
      <span
        {...attrs}
      >
        {content}
      </span>
    );
  };

  getProgressBar = (progress: number, cls: string, labelCls: string, labelPosition: string) => {
    let { cancelable, hidePercentage, infiniteAnimate } = this.props;
    let progressLabel = null;
    let style = {};
    if (!infiniteAnimate) {
      if (this.isCanceled(progress) || progress === 100) {
        cancelable = false;
      }
      progressLabel = (
        <div
          className={labelCls}
        >
          {hidePercentage ? null : this.getProgressContent(progress)}
        </div>
      );
      style = { width: progress + '%' };
    }

    const cancelableAttributes = {
      // <debug>
      'data-cy': 'ProgressBar-cancel'
      // </debug>
    };
    return (
      <>
        <div className={cls}>
          {_.includes(['right', 'bottom', 'top'], labelPosition) ? progressLabel : null}
          <div className='tp-progressbar'>
            <div className={'tp-progress'} style={style}>
              {labelCls === 'inside' ? progressLabel : null}
            </div>
          </div>
        </div>
        {cancelable ? <div
          className={'tp-icon tp-x'}
          {...cancelableAttributes}
          onClick={this.cancelRequest}
        /> : null}
      </>
    );
  };

  private getUploadGroupAndFileName = (): UPLOAD_FILE_DATA => {
    let { fileName = null, group = null, infiniteAnimate } = this.props;
    // <debug>
    if (!infiniteAnimate) {
      // infinite animate is used in download mode
      if (!fileName) {
        throw 'ProgressBar module requires "fileName" prop';
      }
      if (!group) {
        throw 'ProgressBar module requires "group" prop which represents upload group id of file which is uploading.';
      }
    }
    // </debug>
    return {
      fileName,
      uploadGroup: group
    };
  };

  render = () => {
    let { cancelable = false } = this.props;
    const { file = null, progressLabelPosition, progressLabelPackRight, showFileName, infiniteAnimate, keepPercentagesWhenUploaded } = this.props;
    const { fileName, uploadGroup } = this.getUploadGroupAndFileName();
    let progressBar;
    let isUploaded = false;

    let progress: number = 0;

    if (!infiniteAnimate) {
      let { progresses = {} } = this.state;

      if (_.isEmpty(progresses)) {
        progresses = this.props.progresses || {};
      }
      progress = progresses[fileName] || _.get(progresses[uploadGroup], fileName) || this.lastProgress || 0;
      this.lastProgress = progress;
      isUploaded = progress === 100;
      if (file && !isUploaded) {
        isUploaded = file.uploaded === 'alreadyUploaded' || file.isAlreadyUpload;
      }
      progressBar = <span>{progress} %</span>;
      if (this.isCanceled(progress) || progress === 100 || (file && file.isAlreadyUpload)) {
        cancelable = false; // upload is canceled
        progressBar = this.getProgressContent(progress);
      }
    }

    let render;

    const showLabel = progressLabelPosition !== 'none';
    if (showLabel) {
      const progressClasses = classNames('tp-progressbar-wrap', {
        'tp-label-inside': progressLabelPosition === 'inside',
        'tp-label-right': progressLabelPosition === 'right',
        'tp-label-bottom': progressLabelPosition === 'bottom',
        'tp-label-top': progressLabelPosition === 'top',
        'tp-infinite-animation-progressbar': infiniteAnimate
      });
      const progressLabelPack = classNames('tp-label tp-label-progressbar', {
        'tp-label-right': progressLabelPackRight
      });
      progressBar = this.getProgressBar(progress, progressClasses, progressLabelPack, progressLabelPosition);
    }
    if (showFileName) {
      render = (
        <div
          className={this.props.className}
          style={{
            display: 'flex',
            justifyContent: 'space-between'
          }}
        >
          <span className={'tp-icon tp-insert_drive_file'} />
          {showLabel ? this.translate(isUploaded ? 'fileIsUploaded' : 'fileIsUploading', {
            fileName
          }) : fileName}
          {progressBar}
        </div>
      );
    } else {
      render = isUploaded ? (
        <>
          <div
            className={this.props.className}
          >
            {this.translate(file !== null ? file.uploaded : 'uploaded')}
          </div>
          {cancelable ? <div
            className={'tp-icon tp-x'}
            onClick={this.cancelRequest}
          /> : null}
        </>
      ) : (
        progressBar
      );
    }
    if (keepPercentagesWhenUploaded) {
      render = progressBar;
    }

    return render;
  };

  /**
   * Updates percentage.
   * @param progresses
   */
  updateProgress = (progresses: ProgressType) => {
    this.setState({
      progresses: {
        ...this.state.progresses,
        ...progresses
      }
    });
  };

  componentWillUnmount(): void {
    const { infiniteAnimate } = this.props;
    if (!infiniteAnimate) {
      const { fileName, uploadGroup } = this.getUploadGroupAndFileName();
      this.lastProgress = UPLOAD_REQUEST_CANCELED;
      Dispatcher.cancelRequest(fileName, uploadGroup, true);
    }
    super.componentWillUnmount();
  }
}

export default ProgressBar;
